import { Autocomplete, Button, Chip, CircularProgress, Link, OutlinedInput, Stack } from "@mui/material";
import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import Modal, { CommonModalProps } from "..";

import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
    Accordion,
    AccordionDetails,
    AccordionProps,
    AccordionSummary,
    Box,
    BoxProps,
    Typography,
} from "@mui/material";
import { GridColDef, GridRowsProp } from "@mui/x-data-grid-premium";
import { thumbnailService } from "../../../Core/Api/services";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import {
    GetSelectedEpidodes,
    GetTopologyScheme,
    SelectAllEpisodesOfTitle,
    SetTitleSelectedEpisodes,
    UnselectAllEpisodesOfTitle,
} from "../../../Redux/Reducers/redux-slice-new-request";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { GetEnumValueLabelMap } from "../../../Utils/helpers";
import AssetTypeChip from "../../Misc/Chips/AssetChip/asset-type-chip";
import PosterImage from "../../Misc/PosterImage";
import DataTable, { CellSize } from "../../Table/table";
import TableCellStringExpend from "../../Table/TableCellStringExpend/table-cell-string-expend";
import { useTranslation } from "react-i18next";

export type ModalNewRequestCheckElementProps = {
    onConfirm: () => void;
};
export type ModalNewRequestCheckProps = CommonModalProps<ModalNewRequestCheckElementProps>;

const ModalNewRequestCheck = forwardRef<any, ModalNewRequestCheckProps>((props, ref) => {
    const { t } = useTranslation();
    const { onConfirm, closeModal } = props;

    const dispatch = useAppDispatch();

    const topology = useAppSelector(GetTopologyScheme);
    const selectedEpisodes = useAppSelector(GetSelectedEpidodes);

    const [isLoading, setIsLoading] = useState(true);

    let _rows: GridRowsProp = [];
    type Row = (typeof _rows)[number];

    const columns: GridColDef[] = useMemo(
        () => [
            {
                headerName: t("common:type"),
                field: "type",
                minWidth: CellSize.medium,
                flex: CellSize.medium,
                type: "singleSelect",
                valueOptions: GetEnumValueLabelMap(ObjectType),
                renderCell: (data: Row) => <AssetTypeChip type={data.value} />,
            },
            {
                headerName: t("common:name"),
                field: "name",
                minWidth: CellSize.medium,
                flex: CellSize.medium,
                renderCell: (data: Row) => <TableCellStringExpend data={data} />,
            },
            {
                headerName: t("table:headerName.isNew"),
                field: "isNew",
                minWidth: CellSize.medium,
                flex: CellSize.medium,
                renderCell: (data: Row) =>
                    data.value ? (
                        <Chip size="small" label={t("common:new")} color="success" />
                    ) : (
                        <Chip size="small" label={t("common:duplicate")} />
                    ),
            },
        ],
        []
    );

    const handleOnConfirm = () => {
        closeModal();
        onConfirm();
    };

    const getSelectedEpisodesByTitleId = useCallback(
        (titleId: number): number[] => {
            const value = selectedEpisodes.find((t) => t.titleId === titleId)?.selectedEpisodes || [];
            return value;
        },
        [selectedEpisodes]
    );

    useEffect(() => {
        if (topology.length > 0) setIsLoading(false);
        else setIsLoading(true);
    }, [topology]);

    return (
        <Modal
            reference={ref}
            title={t("modal:title.pleaseVerifyYourRequests")}
            closeModal={closeModal}
            width={800}
            footer={
                <Stack direction="row" justifyContent="space-between">
                    <Button variant="outlined" onClick={closeModal}>
                        {t("common:close")}
                    </Button>
                    <Button variant="outlined" color="success" onClick={handleOnConfirm}>
                        {t("common:confirm")}
                    </Button>
                </Stack>
            }
        >
            {isLoading ? (
                <CircularProgress />
            ) : (
                <>
                    {topology.map((title) => (
                        <BoxAccordion
                            key={title.name}
                            title={title.name}
                            leftContent={
                                <PosterImage
                                    width={44}
                                    height={60}
                                    img={{ src: thumbnailService.GetObjectPosterUrl(title.id, ThumbnailSize.small) }}
                                    sx={{ cursor: "pointer" }}
                                />
                            }
                            p={0}
                            m={0}
                        >
                            {title.isSeason && (
                                <>
                                    <Typography mb={1} component="span" color="text.primary">
                                        {t("modal:body.pleaseSelectEpisode")}
                                    </Typography>
                                    <Autocomplete
                                        fullWidth
                                        options={Array.from({ length: title.totalEpisodes }, (item, index) => (item = index + 1))}
                                        value={getSelectedEpisodesByTitleId(title.id)}
                                        onChange={(e, value) =>
                                            dispatch(SetTitleSelectedEpisodes({ titleId: title.id, selectedEpisodes: value }))
                                        }
                                        popupIcon={<ExpandMoreRoundedIcon />}
                                        multiple
                                        getOptionLabel={(option) => String(option)}
                                        renderInput={(params) => (
                                            <OutlinedInput
                                                {...params.InputProps}
                                                inputProps={params.inputProps}
                                                required
                                                sx={{ width: "100%" }}
                                            />
                                        )}
                                    />
                                    <Stack mt={2} direction="row" justifyContent="flex-end" spacing={1}>
                                        <Link
                                            underline="hover"
                                            variant="body2"
                                            color="primary.text"
                                            onClick={() => dispatch(SelectAllEpisodesOfTitle(title.id))}
                                        >
                                            {t("common:selectAllUpperCase")}
                                        </Link>
                                        <Link
                                            underline="hover"
                                            variant="body2"
                                            color="primary.text"
                                            onClick={() => dispatch(UnselectAllEpisodesOfTitle(title.id))}
                                        >
                                            {t("common:unselectAllUpperCase")}
                                        </Link>
                                    </Stack>
                                </>
                            )}
                            {title.contents
                                .filter((c) => c.assets.length > 0)
                                .map((content) => (
                                    <BoxAccordion
                                        key={content.name}
                                        title={`${t("common:content")} ${content.name}`}
                                        rightContent={
                                            content.isNew ? (
                                                <Chip size="small" label={t("common:new")} color="success" sx={{ mr: 1 }} />
                                            ) : undefined
                                        }
                                        p={0}
                                        m={0}
                                    >
                                        <DataTable
                                            noControls
                                            columns={columns}
                                            rows={content.assets}
                                            pagination={false}
                                            disableRowSelectionOnClick
                                            sx={{ p: 0, m: 0 }}
                                        />
                                    </BoxAccordion>
                                ))}
                        </BoxAccordion>
                    ))}
                </>
            )}
        </Modal>
    );
});

export default ModalNewRequestCheck;

interface BoxAccordionProps extends BoxProps {
    title: string;
    leftContent?: React.ReactNode;
    rightContent?: React.ReactNode;
    accordionProps?: AccordionProps;
    defaultExpanded?: boolean;
}

const BoxAccordion: React.FC<BoxAccordionProps> = (props) => {
    const { title, leftContent, rightContent, children, defaultExpanded = true, accordionProps, ...otherProps } = props;

    return (
        <Box component="section" mb={3} {...otherProps}>
            <Accordion defaultExpanded={defaultExpanded} {...accordionProps}>
                <AccordionSummary
                    expandIcon={<ExpandMoreRoundedIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                    sx={{ ".MuiAccordionSummary-content": { justifyContent: "space-between", mr: 1 } }}
                >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        {leftContent}
                        <Typography variant="subtitle2" component="h3" fontWeight={600}>
                            {title}
                        </Typography>
                    </Stack>
                    {rightContent}
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 3, pb: 3 }}>{children}</AccordionDetails>
            </Accordion>
        </Box>
    );
};
